import Phaser from 'phaser';
import {
  ASSET_KEYS,
  COLORS,
  FONTS,
  GAME_HEIGHT,
  GAME_WIDTH,
  MORGEN_RUNDTUR_DURATION_PER_ETASJE,
  MORGEN_RUNDTUR_REKKEFOLGE,
  fontStr,
} from '@/config';
import { sesongForDag, SESONG_NAVN, dagIAaret, aaret } from '@/systems/sesong';
import { foretrekkerRedusertBevegelse } from '@/systems/motionPreferanser';

const PANEL_WIDTH = 360;

export class MorgenIntro {
  private layer?: Phaser.GameObjects.Container;
  private kjorer = false;

  constructor(private scene: Phaser.Scene) {}

  spill(dag: number, onDone?: () => void): void {
    if (this.kjorer) {
      onDone?.();
      return;
    }
    this.kjorer = true;

    if (foretrekkerRedusertBevegelse()) {
      this.spillRedusert(dag, onDone);
      return;
    }

    const romBredde = GAME_WIDTH - PANEL_WIDTH;
    this.layer = this.scene.add.container(0, 0).setDepth(60);

    const bakgrunn = this.scene.add
      .rectangle(0, 0, romBredde, GAME_HEIGHT, COLORS.beige, 1)
      .setOrigin(0, 0);
    this.layer.add(bakgrunn);

    const bilde = this.scene.add
      .image(romBredde / 2, GAME_HEIGHT / 2, ASSET_KEYS.envResepsjon)
      .setOrigin(0.5);
    this.skaler(bilde, romBredde);
    bilde.setAlpha(0);
    this.layer.add(bilde);

    const overlay = this.scene.add
      .rectangle(0, 0, romBredde, GAME_HEIGHT, 0x1a1a1a, 0.35)
      .setOrigin(0, 0);
    this.layer.add(overlay);

    const sesong = sesongForDag(dag);
    const sesongNavn = SESONG_NAVN[sesong];
    const aarNr = aaret(dag);
    const dagiAar = dagIAaret(dag);

    const dagTekst = this.scene.add
      .text(romBredde / 2, GAME_HEIGHT / 2 - 110, `Dag ${dag}`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(120),
        color: '#ffffff',
        fontStyle: '700',
      })
      .setOrigin(0.5)
      .setAlpha(0);
    this.layer.add(dagTekst);

    const sesongTekst = this.scene.add
      .text(
        romBredde / 2,
        GAME_HEIGHT / 2 + 70,
        `${sesongNavn} — år ${aarNr} (dag ${dagiAar})`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(34),
          color: '#f4ead5',
          wordWrap: { width: romBredde - 80 },
          align: 'center',
        },
      )
      .setOrigin(0.5)
      .setAlpha(0);
    this.layer.add(sesongTekst);

    this.scene.tweens.add({
      targets: bilde,
      alpha: 1,
      duration: 200,
    });
    this.scene.tweens.add({
      targets: [dagTekst, sesongTekst],
      alpha: 1,
      duration: 320,
      delay: 120,
    });

    const rekkefolge = [...MORGEN_RUNDTUR_REKKEFOLGE];
    let i = 0;
    const skiftBilde = () => {
      i += 1;
      if (i >= rekkefolge.length) {
        this.scene.time.delayedCall(MORGEN_RUNDTUR_DURATION_PER_ETASJE, () => {
          this.scene.tweens.add({
            targets: this.layer,
            alpha: 0,
            duration: 320,
            onComplete: () => {
              this.layer?.destroy(true);
              this.layer = undefined;
              this.kjorer = false;
              onDone?.();
            },
          });
        });
        return;
      }
      const key = ASSET_KEYS[rekkefolge[i]];
      bilde.setTexture(key);
      this.skaler(bilde, romBredde);
      bilde.setAlpha(0);
      this.scene.tweens.add({
        targets: bilde,
        alpha: 1,
        duration: 220,
      });
      this.scene.time.delayedCall(MORGEN_RUNDTUR_DURATION_PER_ETASJE, skiftBilde);
    };
    this.scene.time.delayedCall(MORGEN_RUNDTUR_DURATION_PER_ETASJE, skiftBilde);
  }

  private spillRedusert(dag: number, onDone?: () => void): void {
    const romBredde = GAME_WIDTH - PANEL_WIDTH;
    this.layer = this.scene.add.container(0, 0).setDepth(60);

    const bakgrunn = this.scene.add
      .rectangle(0, 0, romBredde, GAME_HEIGHT, 0x1a1a1a, 0.7)
      .setOrigin(0, 0);
    this.layer.add(bakgrunn);

    const sesong = sesongForDag(dag);
    const sesongNavn = SESONG_NAVN[sesong];
    const aarNr = aaret(dag);
    const dagiAar = dagIAaret(dag);

    const dagTekst = this.scene.add
      .text(romBredde / 2, GAME_HEIGHT / 2 - 80, `Dag ${dag} starter`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(80),
        color: '#ffffff',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    this.layer.add(dagTekst);

    const sesongTekst = this.scene.add
      .text(
        romBredde / 2,
        GAME_HEIGHT / 2 + 60,
        `${sesongNavn} — år ${aarNr} (dag ${dagiAar})`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(30),
          color: '#f4ead5',
          wordWrap: { width: romBredde - 80 },
          align: 'center',
        },
      )
      .setOrigin(0.5);
    this.layer.add(sesongTekst);

    this.scene.time.delayedCall(900, () => {
      this.layer?.destroy(true);
      this.layer = undefined;
      this.kjorer = false;
      onDone?.();
    });
  }

  private skaler(bilde: Phaser.GameObjects.Image, romBredde: number): void {
    const tex = bilde.texture.getSourceImage() as HTMLImageElement;
    const w = tex.width || 1;
    const h = tex.height || 1;
    const scale = Math.max(romBredde / w, GAME_HEIGHT / h);
    bilde.setScale(scale);
  }
}
